// src/pages/MyCoursesPage.jsx
"use client";

import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import {
  useGetCoursesQuery,
  useGetCourseProgressQuery,
} from "@/features/api/courseApi";

import { useGetMyOrdersQuery } from "@/features/api/paymentApi";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { BookOpen, Video, CheckCircle } from "lucide-react";

export default function MyCoursesPage() {
  const navigate = useNavigate();
  const user = useSelector((s) => s.auth.user);

  /* ---------------- AUTH ---------------- */
useEffect(() => {
  if (user === null) navigate("/login");
}, [user, navigate]);

  /* ---------------- ORDERS ---------------- */
  const {
    data: ordersRes,
    isLoading: ordersLoading,
    isError: ordersError,
    refetch,
  } = useGetMyOrdersQuery(undefined, { skip: !user });

  /* ---------------- COURSES ---------------- */
  const { data: coursesRes, isLoading: coursesLoading } =
    useGetCoursesQuery(undefined, { skip: !user });

  /* ---------------- PROGRESS ---------------- */
  const { data: progressRes, isLoading: progressLoading } =
    useGetCourseProgressQuery(undefined, { skip: !user });

  const orders = ordersRes?.data || [];
  const courses = coursesRes?.courses || [];
  const progressList = progressRes?.progress || [];

  /* ---------------- MAP PROGRESS ---------------- */
  const progressMap = useMemo(() => {
    return progressList.reduce((acc, p) => {
      acc[p.courseId] = p;
      return acc;
    }, {});
  }, [progressList]);

  /* ---------------- OWNED IDS ---------------- */
const ownedIds = useMemo(() => {
  const paid = orders.map((o) => o.courseId).filter(Boolean);
  const free = user?.enrolledCourses || [];

  return new Set([...paid, ...free].map(String));
}, [orders, user]);

  /* ---------------- MY COURSES ---------------- */
const myCourses = useMemo(() => {
  return courses.filter((c) => ownedIds.has(String(c._id)));
}, [courses, ownedIds]);

  useEffect(() => {
    if (ordersError) toast.error("Failed to load your courses");
  }, [ordersError]);

  /* ---------------- SKELETON ---------------- */
  const CourseSkeleton = () => (
    <div className="space-y-3">
      <Skeleton className="h-40 w-full rounded-lg" />
      <Skeleton className="h-4 w-3/4" />
      <Skeleton className="h-4 w-1/2" />
      <Skeleton className="h-10 w-full" />
    </div>
  );

  if (ordersLoading || coursesLoading || progressLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 p-10">
        {Array.from({ length: 8 }).map((_, i) => (
          <CourseSkeleton key={i} />
        ))}
      </div>
    );
  }

  if (!myCourses.length) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        You haven’t enrolled in any courses yet.
      </div>
    );
  }

  /* ---------------- UI ---------------- */
  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-6 py-10">

        <h1 className="text-3xl font-bold text-center mb-10">
          🎓 My Courses
        </h1>

        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">

          {myCourses.map((course) => {
            const progress = progressMap[course._id] || {};
            const percentage = progress.percentage || 0;
            const lastLesson = progress.lastLessonSlug;

            const completed = percentage === 100;

            const buttonText = completed
              ? "Completed"
              : percentage > 0
              ? "Resume"
              : "Start";

            const targetUrl =
              lastLesson && percentage > 0
                ? `/courses/${course.slug}/learn/${lastLesson}`
                : `/courses/${course.slug}/learn`;

            return (
              <Card
                key={course._id}
                className="overflow-hidden hover:shadow-lg transition flex flex-col relative"
              >
                {/* Completion Badge */}
                {completed && (
                  <div className="absolute top-2 right-2 bg-green-600 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
                    <CheckCircle className="h-3 w-3" />
                    Completed
                  </div>
                )}

                {course.featuredImage ? (
                  <img
                    src={
                      typeof course.featuredImage === "string"
                        ? course.featuredImage
                        : course.featuredImage?.url || ""
                    }
                    className="h-40 w-full object-cover"
                    alt={course.title}
                  />
                ) : (
                  <div className="h-40 flex items-center justify-center bg-muted">
                    <Video className="h-10 w-10 text-muted-foreground" />
                  </div>
                )}

                <CardHeader className="p-4">
                  <h2 className="font-semibold line-clamp-2 text-lg">
                    {course.title}
                  </h2>

                  <p className="text-sm text-muted-foreground mt-1">
                    {course.instructorName || "Instructor"}
                  </p>
                </CardHeader>

                <CardContent className="p-4 flex flex-col gap-3 mt-auto">

                  {/* Progress */}
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span>Progress</span>
                      <span>{percentage}%</span>
                    </div>
                    <Progress value={percentage} />
                  </div>

                  <Button
                    className="w-full mt-2"
                    onClick={() => navigate(targetUrl)}
                  >
                    <BookOpen className="mr-2 h-4 w-4" />
                    {buttonText}
                  </Button>
                </CardContent>
              </Card>
            );
          })}

        </div>
      </div>
    </div>
  );
}